import { useNotification } from "@expert/common-ui";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import { useClipboard } from "@mantine/hooks";
import { useMemo, useState } from "react";

import { useAnalytics } from "@/analytics";
import {
    getIsIdleSession,
    getSelectedSession,
    getSelectedSessionId,
    hasPendingCallback,
    useAgentActivity,
    useAgentSdk,
    useAgentStore,
    usePartner,
    useTypedMostRecentTask,
} from "@/sdk";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { formatAgentName } from "@/shared-utils";
import { userCache } from "@/shared-utils/auth/userCache";

import { env } from "./environment";

interface UseProfileDataProps {
    commitHash: string;
}

export interface ProfileData {
    agentName: string;
    formattedVersionNumber: string;
    menuOpen: boolean;
    setMenuOpen: (open: boolean) => void;
    isProactiveEnabled: boolean;
    isLightModeEnabled?: boolean;
    disableLogOff: boolean | undefined;
    copyToDebug: () => void;
    logOff: () => void;
    handleProfileMenuView: () => void;
    onSoundNotificationsClick: (isMuted: boolean) => void;
}

export function useProfileData({ commitHash }: UseProfileDataProps): ProfileData {
    const logger = useLogger({ module: "Profile" });
    const {
        features: { isProactiveEnabled, isLightModeEnabled },
    } = useFeatures<WorkspaceFeatures>();

    const agentName = useAgentStore((state) => formatAgentName(state.agentName));
    const activity = useAgentStore((state) => state.activity);
    const { dispatcher } = useAnalytics();
    const clipboard = useClipboard({ timeout: 500 });

    const sessionId = getSelectedSessionId();
    const WorkerId = useAgentStore.getState().agentId;
    const Username = useAgentStore.getState().agentName;
    const { voiceTask, chatTask, task } = useTypedMostRecentTask();
    const partner = usePartner();
    const notification = useNotification();
    const agentSdk = useAgentSdk();
    const { setNewAgentActivity } = useAgentActivity();

    const [menuOpen, setMenuOpen] = useState(false);
    const loggerWithContext = useMemo(
        () =>
            logger.child({
                sessionId,
                callSid: voiceTask?.agentCallId,
                chatId: chatTask?.chatId,
            }),
        [logger, sessionId, voiceTask?.agentCallId, chatTask?.chatId],
    );

    const { employeeId, userEmail, identity, givenName, familyName, groups } = userCache;

    const getSessionType = () => {
        if (getIsIdleSession()) return "idle";
        if (voiceTask) return "voice";
        if (chatTask) return "chat";
        return "undefined";
    };

    const copyToDebug = () => {
        clipboard.copy(
            JSON.stringify({
                SessionId: sessionId || "undefined",
                WorkerId: WorkerId || "undefined",
                UserName: Username || "undefined",
                TaskSid: task?.id ?? "undefined",
                SessionType: getSessionType(),
                StartReason: getSelectedSession().metadata.startReason ?? "undefined",
                EmployeeId: employeeId ?? "undefined",
                Email: userEmail ?? "undefined",
                Identity: identity ?? "undefined",
                GivenName: givenName ?? "undefined",
                FamilyName: familyName ?? "undefined",
                Groups: groups ?? "undefined",
                ...(voiceTask
                    ? {
                          CallSid: voiceTask.agentCallId ?? "undefined",
                          Mdn: voiceTask.mdn ?? "undefined",
                          Partner: voiceTask.partner ?? partner ?? "undefined",
                      }
                    : {}),
                ...(chatTask
                    ? {
                          ChatId: chatTask.chatId ?? "undefined",
                          Mdn: chatTask.mdn?.value?.replace(/\D/g, "") ?? "undefined",
                          Queue: chatTask.routingQueue ?? "undefined",
                          SourceApp: chatTask.sourceApp ?? "undefined",
                          Partner: chatTask.partner ?? partner ?? "undefined",
                      }
                    : {}),
                CommitHash: commitHash || "undefined",
                Timestamp: new Date().toISOString(),
            }),
        );
        notification.success("Copied debug info to clipboard!");
        setMenuOpen(false);
    };

    function handleProfileMenuView() {
        void dispatcher.dispatchAction("Click", "agent-profile-menu-view");
    }

    const logOff = async () => {
        await setNewAgentActivity(agentSdk.offlineAgentActivity);
        setMenuOpen(false);
    };

    const onSoundNotificationsClick = (isMuted: boolean) => {
        loggerWithContext.info("Sound notifications toggled");
        void dispatcher.dispatchAction("Click", "sound-notification-toggle", { isMuted });
    };

    const formattedVersionNumber = env.versionNumber.startsWith("v")
        ? `v${env.versionNumber.substring(3)}`
        : env.versionNumber;

    const disableLogoffUnlessInWrapping = task && task?.status !== "wrapping";
    const disableLoggingOffCases =
        agentSdk.serviceProvider === "connect" && (hasPendingCallback() || disableLogoffUnlessInWrapping);
    const disableLogOff = activity === agentSdk.offlineAgentActivity || disableLoggingOffCases;

    return {
        agentName,
        formattedVersionNumber,
        menuOpen,
        setMenuOpen,
        isProactiveEnabled,
        isLightModeEnabled,
        disableLogOff,
        copyToDebug,
        logOff: () => void logOff(),
        handleProfileMenuView,
        onSoundNotificationsClick,
    };
}
